import { createRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { css } from 'styled-system/css';
import { type Puzzle } from '@/domain';
// Sanctioned app→module bridge (ADR-0072).
import { PlayScreen } from '@/ui/play/PlayScreen';
import { AppShell } from '@/ui/v2/AppShell';
import { SparrowState } from '@/ui/v2/SparrowState';
import { sparrowFlightScene } from '@/ui/v2/SparrowScenes';
import { INDEXABLE_ROUTES, SITE_BASE_URL, breadcrumbJsonLd, gameJsonLd, indexableHead } from '@/ui/seo';
import { t } from '@/ui/i18n';
import { Route as AppLayoutRoute } from './app-layout';

// Rising sun on the horizon with a sparrow above — the daily "bientôt" state.
const sunriseScene = (
  <svg width="150" height="120" viewBox="0 0 150 120" role="img" aria-label={t('route.play.unavailable.sceneAria')}>
    <defs>
      <symbol id="puBird" viewBox="0 0 64 64">
        <path d="M9 30 L24 33 L20 44 Z" fill="#214B40" />
        <path d="M22 44 C16 41 16 30 21 24 C26 18 35 17 42 21 C46 23 49 27 49 31 L57 29 L49 34 C49 41 43 47 35 47 C30 47 25 46 22 44 Z" fill="#D45D83" />
        <path d="M28 30 C35 29 41 33 42 40 C35 41 29 38 28 30 Z" fill="#BE4970" />
        <path d="M24 42 C27 45 32 45 36 44 C33 47 27 47 24 42 Z" fill="#F6C9D7" />
        <path d="M49 30 L58 31.5 L49 33.5 Z" fill="#D8C77A" />
        <circle cx="44.5" cy="29.5" r="2.4" fill="#fff" />
        <circle cx="45" cy="29.7" r="1.3" fill="#214B40" />
      </symbol>
    </defs>
    <path d="M33 82 A42 42 0 0 1 117 82 Z" fill="#F6C98C" opacity="0.18" />
    <path d="M51 82 A24 24 0 0 1 99 82 Z" fill="#F6C98C" />
    <line x1="8" y1="82" x2="142" y2="82" stroke="#C4E5D3" strokeWidth="3" strokeLinecap="round" />
    <use href="#puBird" x="90" y="28" width="40" height="40" transform="rotate(-8 110 48)" />
  </svg>
);

// No puzzle: the daily isn't generated yet (ADR-0042 → 404), or plain `pnpm dev` has no grid-api (run `pnpm dev:preview` for MSW).
function PlayUnavailable() {
  const navigate = useNavigate();
  return (
    <AppShell variant="flow">
      <SparrowState
        scene={sunriseScene}
        title={t('route.play.unavailable.title')}
        body={t('route.play.unavailable.body')}
        cta={{ label: t('route.play.unavailable.cta'), onClick: () => void navigate({ to: '/grilles' }) }}
      />
    </AppShell>
  );
}

// Thrown = transient failure (network/5xx/SW status-0), not the worker-not-ready 404 that maps to null.
function PlayLoadError() {
  const router = useRouter();
  return (
    <AppShell variant="flow">
      <SparrowState
        scene={sparrowFlightScene()}
        title={t('route.play.loadError.title')}
        body={t('route.play.loadError.body')}
        cta={{ label: t('common.retry'), onClick: () => void router.invalidate() }}
      />
    </AppShell>
  );
}

function PlayRouteComponent() {
  const puzzle = Route.useLoaderData() as Puzzle | null;
  const { puzzleSolver, soloEntriesStore, soundPlayer, soundStore, skipFilledStore, surveyClient } = Route.useRouteContext();
  if (!puzzle) return <PlayUnavailable />;
  return <PlayScreen puzzle={puzzle} puzzleSolver={puzzleSolver} soloEntriesStore={soloEntriesStore} soundPlayer={soundPlayer} soundStore={soundStore} skipFilledStore={skipFilledStore} surveyClient={surveyClient} reportSurface="daily" />;
}

const pendingCss = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'wsUi',
  fontWeight: 'semibold',
  fontSize: '14px',
  color: 'ws.khaki',
  opacity: 0.7,
});

const PLAY_TITLE = INDEXABLE_ROUTES.find((x) => x.path === '/play')!.title;

// Non-grid placeholder during the daily loader; also the prerendered /play body, so the static HTML bakes no grid to flicker.
function PlayPending() {
  // head() runs only after the loader resolves; React 19 hoists this <title> so the prerender hang pass + crawlers see one.
  return (
    <AppShell variant="flow">
      <title>{PLAY_TITLE}</title>
      <div className={pendingCss} role="status" aria-busy="true" aria-label={t('route.play.pending.aria')}>
        {t('route.play.pending.text')}
      </div>
    </AppShell>
  );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createRoute({
  getParentRoute: () => AppLayoutRoute,
  path: 'play',
  // `?date=YYYY-MM-DD` opens that day's grid; any other value resolves to "today".
  validateSearch: (search: Record<string, unknown>): { date?: string } =>
    typeof search.date === 'string' && ISO_DATE.test(search.date) ? { date: search.date } : {},
  loaderDeps: ({ search }) => ({
    date: typeof search.date === 'string' && ISO_DATE.test(search.date) ? search.date : undefined,
  }),
  loader: async ({ context, deps }): Promise<Puzzle | null> => {
    const puzzle = await context.puzzleRepository.fetchDaily(deps.date);
    // Pull this grid's cross-device progress before render so the uncontrolled grid reads the merge (ADR-0075).
    if (puzzle) {
      await context.progressSyncService?.pullAndMergeOne(puzzle.id).catch(() => {});
    }
    return puzzle;
  },
  component: PlayRouteComponent,
  pendingComponent: PlayPending,
  // Show the placeholder immediately so first client render matches the prerendered body (no blank frame, no grid flash).
  pendingMs: 0,
  errorComponent: PlayLoadError,
  head: () => {
    const r = INDEXABLE_ROUTES.find((x) => x.path === '/play')!;
    return {
      ...indexableHead('/play'),
      scripts: [
        {
          type: 'application/ld+json',
          children: breadcrumbJsonLd([
            { name: t('seo.breadcrumb.accueil'), item: `${SITE_BASE_URL}/` },
            { name: r.title, item: `${SITE_BASE_URL}/play` },
          ]),
        },
        {
          type: 'application/ld+json',
          children: gameJsonLd({
            name: t('seo.play.game.name'),
            description: t('seo.play.game.description'),
            url: `${SITE_BASE_URL}/play`,
          }),
        },
      ],
    };
  },
});
